import React, { useState, useEffect, useContext, useRef } from 'react';
import { withAuth } from '@cdk-prod/fortellis-auth-context';
import { APIDetails } from '@cdk-prod/api-directory-component';
import { fortellisTheme } from 'cdk-radial';
import { Page } from '@fortellis/page';
import { Link, useParams, useHistory, useLocation } from 'react-router-dom';
import {
  API_DIRECTORY_THEME,
  API_DETAILS_HANDLERS_MODEL,
  APP_STATUS
} from '../../utils/constants';
import config from '../../config/config';
import axios from 'axios';
import { buildWithTypes, types } from './constants';
import { clearDebounceTimers, trackComparePrice, trackSelectApiVersion, trackAdjustedCalls, trackRequestInfo } from './gtmTracking';
import { useDirectoryApi, useGroupApi, useUserProfile } from '../../hooks/use-query';
import { locationContext } from '../locationContext';
import { FEATURES_FLAGS, isEnabled } from '../../utils/feature-gate-utils';
import FortellisBot from '../common/FortellisBot';
import { sendAmplitudeData } from '../../utils/amplitude';
import {
  getOrGenerateIdentifierId,
  getOrgUnitId,
  getApiVersions,
  normalizeGtmUserRoles
} from '../../utils/gtmUtils';

const solutionUrl = config.api.solutionUrl;

const capitalize = (str = '') =>
  str && str.length >= 2 && str.charAt(1) !== str.charAt(1).toUpperCase()
    ? str.charAt(0).toUpperCase() + str.slice(1)
    : str;

const ApiDetailsWrapper = ({
  store,
  auth,
  dispatch,
  entityContext: { entity, loadingEntity, entities } = {},
  getDomainDisplayName,
  sendEmailToConsumer,
  sendEmailToProvider,
  restrictedRequestAccess
}) => {
  const envURLs = { DOCS_UI_URL: `${config.apidocs.url}` };
  const params = useParams();
  const apiId = params?.id;
  const history = useHistory();
  const location = useLocation();
  const { setCurrentLocation } = useContext(locationContext);
  const lastPushedEvent = useRef(null);
  const [appDataLoadStarted, setAppDataLoadStarted] = useState(false);
  const [appDataLoading, setAppDataLoading] = useState(true);
  const [appData, setAppData] = useState([]);
  const [groupData, setGroupData] = useState({ data: [], isFetching: true });
  const { apiInfo } = store;
  const { roles: userRoles, isLoading: isProfileLoading } = useUserProfile(auth);

  useEffect(() => {
    return () => {
      clearDebounceTimers();
    };
  }, []);

  const {
    isAuthenticated,
    userData: { name: userName, email: userEmail } = {}
  } = auth;
  const { id: entityId, name: entityName } = entity || {};

  const authProp = {
    ...auth,
    isAuthenticated,
    userData: {
      name: userName,
      email: userEmail
    },
    orgData: {
      id: entityId,
      name: entityName
    }
  };

  useDirectoryApi(auth);
  const {
    data: domainData,
    isLoading: isDomainLoading,
    isFetching: isDomainFetching
  } = useGroupApi(auth);

  if (!(isDomainLoading || isDomainFetching)) {
    if (groupData.isFetching) {
      setGroupData({
        data: domainData?.items.find(
          f => f?.name?.toLowerCase() === 'categories'
        )?.dimensions,
        loading: false
      });
    }
  }

  useEffect(() => {
    if (auth?.accessToken && !appDataLoadStarted) {
      setAppDataLoadStarted(true);
      axios({
        method: 'GET',
        url: `${solutionUrl}/v1/solutions/?includeTest=false`,
        headers: {
          Authorization: `Bearer ${auth?.accessToken}`
        }
      })
        .then(response => {
          const appData = response?.data
            ?.filter(app => app?.status !== APP_STATUS.CANCELLED)
            .map(app => ({
              displayName: app?.displayName,
              id: app?.id,
              disabledFeatures: app?.disabledFeatures || []
            }));
          setAppData(appData);
          setAppDataLoading(false);
        })
        .catch(error => {
          console.error('failed to get app details');
          setAppDataLoading(false);
        });
    } else if (auth?.loginPending === false && !auth?.isAuthenticated) {
      // we know for sure we're not logged in
      setAppDataLoading(false);
    }
  }, [
    auth?.accessToken,
    auth?.loginPending,
    auth?.isAuthenticated,
    appDataLoadStarted
  ]);

  useEffect(() => {
    // Wait for auth to be fully initialized
    if (
      typeof auth?.isAuthenticated !== 'boolean' ||
      auth?.loginPending
    ) {
      return;
    }

    if (
      !apiInfo?.isFetching &&
      apiInfo?.data &&
      !apiInfo?.error
    ) {
      if (auth?.isAuthenticated) {
        const isEntityLoading =
          loadingEntity === undefined || loadingEntity === true;
        const isMissingExpectedOrgDetails = auth?.entityId && !entity?.name;

        if (isEntityLoading || isMissingExpectedOrgDetails || isProfileLoading) {
          return;
        }
      }

      let data = apiInfo.data;
      if (typeof apiInfo.data === 'string') {
        try {
          data = JSON.parse(apiInfo.data);
        } catch (e) {
          data = {};
        }
      }

      if (Array.isArray(data)) {
        data = data[0] || {};
      }

      const currentEntity = entity || {};
      const fortellisId = auth?.isAuthenticated
        ? currentEntity.id || auth?.entityId || null
        : null;
      const orgName = auth?.isAuthenticated ? currentEntity.name || null : null;
      const rolesPayload = auth?.isAuthenticated
        ? normalizeGtmUserRoles(userRoles)
        : null;
      const userId = auth?.isAuthenticated
        ? auth?.userData?.sub || null
        : null;
      const identifierId = getOrGenerateIdentifierId();
      const orgUnitId = auth?.isAuthenticated
        ? getOrgUnitId(currentEntity, entities, fortellisId)
        : null;
      const storeId = orgUnitId || null;
      const storeName = orgName || null;

      const pageSubsection = location.pathname.startsWith('/async-apis/')
        ? 'Async API Details'
        : 'API Details';

      const { prodVersions, testVersions } = getApiVersions(
        data,
        auth?.isAuthenticated
      );

      const currentEventKey = `${location.pathname}-${userId || ''}-${fortellisId || ''}-${apiId || ''}-${
        rolesPayload ? rolesPayload.join(',') : ''
      }`;
      if (lastPushedEvent.current !== currentEventKey) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'fortellis-pageview',
          page_section: 'API Directory',
          page_subsection: pageSubsection,
          directory_type: 'ISV',
          api_id: apiId,
          api_name: data.name || null,
          api_type: data.kind || null,
          api_prod_version: prodVersions,
          api_test_version: testVersions,
          identifier_id: identifierId,
          fortellis_user_id: userId,
          user_id: null,
          enterprise_id: null,
          enterprise_name: null,
          company_id: null,
          store_id: storeId,
          store_name: storeName,
          stores: null,
          product_features: null,
          user_org: orgName,
          user_role: rolesPayload,
          page_url: window.location.href || null,
          page_path: location.pathname || null,
          is_impersonated_session: false,
          fortellis_id: fortellisId,
          fortellis_org_name: orgName,
          product: 'API',
          application: 'Fortellis Network'
        });

        lastPushedEvent.current = currentEventKey;
      }
    }
  }, [
    apiInfo,
    auth,
    entity,
    loadingEntity,
    entities,
    apiId,
    location.pathname,
    userRoles,
    isProfileLoading
  ]);

  const getBreadcrumbs = apiName => {
    const urlParams = new URLSearchParams(window.location.search);
    const storedFilters = sessionStorage.getItem('selectedFilters');
    const hasActiveFilters = urlParams.toString() || storedFilters;
    const apiDirectoryUrl = hasActiveFilters
      ? `/?${storedFilters || urlParams.toString()}`
      : '/';

    return [{ text: 'API Directory', url: apiDirectoryUrl }, { text: apiName }];
  };

  const onFilterClick = ({ newUrl, props }) => {
    history.push(newUrl, { ...props });
  };

  const buildWithApi = ({ appId, buildWithType, apiId, apiType }) => {
    if (buildWithType === buildWithTypes.NEW_APP) {
      window.open(
        `${config.developer.url}developer-account/solutions/register?api=${apiId}`
      );
    } else {
      window.open(
        `${config.developer.url}developer-account/solutions/edit/${appId}?api=${apiId}`
      );
    }
  };

  const handleRequestAccess = async ({
    setIsRequesting,
    setIsRequested,
    setShowSuccessToast
  }) => {
    let data = apiInfo?.data;
    if (typeof apiInfo?.data === 'string') {
      try {
        data = JSON.parse(apiInfo.data);
      } catch (e) {
        data = {};
      }
    }
    if (Array.isArray(data)) {
      data = data[0] || {};
    }
    const isAsync =
      location.pathname.startsWith('/async-apis/') ||
      data?.kind === 'async-api';
    const apiType = isAsync ? 'async' : 'rest';

    trackRequestInfo({ 
      apiName: data?.name || title || '',
      apiId,
      apiType
    });

    try {
      setCurrentLocation();
      setIsRequesting(true);
      const result = await restrictedRequestAccess(apiId);
      if (result) {
        setIsRequested(true);
        setShowSuccessToast(true);
        dispatch({
          type: types.UPDATE_RESTRICTED_STATUS,
          status: 'requested'
        });
      } else {
        console.error('Request failed');
      }
    } catch (err) {
      console.error('Request access failed', err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handlers = {
    ...API_DETAILS_HANDLERS_MODEL,
    apiTypeFilter: onFilterClick,
    apiCategoryFilter: onFilterClick,
    buildWithApi: buildWithApi,
    sendEmailToConsumer: sendEmailToConsumer,
    sendEmailToProvider: sendEmailToProvider,
    restrictedRequestAccess: restrictedRequestAccess,
    handleRequestAccess: handleRequestAccess,
    trackComparePrice,
    trackSelectApiVersion,
    trackAdjustedCalls,
    trackRequestInfo
  };

  const title = apiInfo.data?.name || '';
  const domainDisplayNames = groupData.data;
  let specError;
  const requestingOrg = auth?.entityId;

  if (typeof apiInfo.data === 'string') {
    try {
      const data = JSON.parse(apiInfo.data);
      specError = Array.isArray(data) ? data[0]?.error : data.error;
    } catch (error) {
      console.error('Failed to parse API Specification', error);
    }
  }

  if (apiInfo.error || specError) {
    return (
      <div className="error-page error-title">{apiInfo.error || specError}</div>
    );
  }

  return (
    <>
      {!apiInfo.isFetching && apiInfo.data && (
        <Page
          breadcrumbs={getBreadcrumbs(capitalize(title))}
          routerLink={Link}
        />
      )}
      <APIDetails
        apiId={apiId}
        apiInfo={apiInfo}
        entity={entity}
        fetchingAPIDetails={apiInfo.isFetching}
        errorInFetchingAPIDetails={false}
        apiDetailsOnClickHandlers={handlers}
        cartDetails={[]}
        orderDetails={[]}
        domainDisplayNames={domainDisplayNames}
        auth={authProp}
        belongsTo={API_DIRECTORY_THEME.ISV_API_DIRECTORY}
        sendAmplitudeData={sendAmplitudeData}
        environmentURLs={envURLs}
        sendingEmailToConsumer={sendEmailToConsumer?.isFetching}
        sendingEmailToProvider={sendEmailToProvider?.isFetching}
        restrictedRequestAccess={restrictedRequestAccess}
        requestingOrg={requestingOrg}
        existingSolutionDetails={appData}
        disableBuildNow={appDataLoading}
      />
      <FortellisBot auth={auth} entity={entity} />
    </>
  );
};

export default withAuth(ApiDetailsWrapper);
